// src/cockpit4/components/AIMarketplacePanel.tsx
// ============================================
// AI 超市 — 产品卡片货架（v5.0 P1 重构）
//
// 定位：企业所有 AI 能力的统一入口
// 风格：App Store 产品卡片，而非项目进度看板
// 6 个独立产品 + 1 个后台能力
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import {
  Store, ExternalLink, X, Play, Zap, CheckCircle2, TrendingUp,
  Building2, Coins, Sparkles, Brain, Factory,
  ArrowUpRight, Globe, Activity, Layers, SearchCode, ArrowLeft,
  // v6.3.16 官网工具矩阵图标
  Eye, ScanSearch, AudioLines, Grid3x3,
  // v6.7 新增官网引流工具
  ScanLine, Pickaxe,
  // P2：问题→方案入口（AgentMesh 路由）
  Route as RouteIcon, Send, Loader2,
} from "lucide-react";
// v5.2：GEO+DSO 控制台（含一键启动 GrowthFactory 编排）——在 AI 超市内直接打开
import GEODSOEntry from "./GEODSOEntry";
// v6.6：角色门控（超级管理员可见受限产品，对外不展示量化/国学）
import { useUserStore } from "../../stores/userStore";
// P2：AgentMesh 意图路由（零 token）——"问题→方案"能力推荐
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// P2：AgentMesh 路由结果类型（对齐 AgentMesh/types.ts RouterDecision）
// ============================================

interface RouteDecision {
  agentId: string;
  agentName: string;
  role: string;
  score: number;
  reason: string;
}

/** 角色中文标签（对齐 AgentMeshConsole） */
const ROLE_LABEL: Record<string, string> = {
  planner: "规划", researcher: "研究", writer: "写作",
  reviewer: "审阅", analyst: "分析", executor: "执行", quality: "质检",
};

// ============================================
// P2 深化：AgentMesh 路由决策 → AI 超市产品/能力卡联动映射
// 路由命中的 Agent 能力 → 对应产品卡；无映射的 Agent 保持纯信息展示
// ============================================
const AGENT_TO_PRODUCT: Record<string, string> = {
  planner: "geo_dso",              // 目标拆解 → GEO+DSO 增长工具
  researcher: "viral_analysis",    // 调研/选题/素材 → 爆款分析引擎
  content_strategy: "geo_matrix",  // 内容策略/角度 → GEO 关键词矩阵
  copywriting: "voice_clone",      // 文案写作 → 口吻克隆
  xhs: "voice_clone",              // 小红书图文 → 口吻克隆（全平台文案）
  wechat: "voice_clone",           // 公众号文案 → 口吻克隆
  douyin: "viral_analysis",        // 抖音脚本 → 爆款分析引擎（脚本模板/标题公式）
  video: "content_factory",        // 视频/数字人 → 内容工厂（后台能力）
  image: "content_factory",        // 封面/配图 → 内容工厂（后台能力）
  quality: "content_factory",      // 质检 → 内容工厂（审核工作流）
};

// ============================================
// 产品定义（v5.0 白皮书对齐）
// ============================================

interface ProductCardData {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  icon: typeof Store;
  color: string;
  category: "product" | "capability";
  status: "可调用" | "Beta" | "规划中" | "运行中";
  launchUrl: string;
  tags: string[];
  stats?: {
    label: string;
    value: string;
  }[];
  hermeneCall: string;
}

const PRODUCTS: ProductCardData[] = [
  {
    id: "hotel_saas",
    name: "酒店 SaaS",
    subtitle: "Hotel Digital Operations",
    description: "酒店行业数字化管理平台，覆盖预订、入住、客房、餐饮全流程。AI 驱动的智能运营决策助手。",
    icon: Building2,
    color: "#38BDF8",
    category: "product",
    status: "可调用",
    launchUrl: "https://os.xshetech.com",
    tags: ["SaaS", "酒店", "数字化"],
    stats: [
      { label: "Runtime", value: "Hotel" },
      { label: "状态", value: "运行中" },
    ],
    hermeneCall: '"帮我分析酒店日报" → Hotel Runtime',
  },
  {
    id: "gold_forex_quant",
    name: "量化交易 · 黄金外汇",
    subtitle: "Gold & Forex Quantitative Trading",
    description: "黄金外汇量化交易系统：多策略回测、实时信号、风控止盈止损、跨市场套利。覆盖 XAU/USD、主要货币对。",
    icon: Coins,
    color: "#F59E0B",
    category: "product",
    status: "可调用",
    launchUrl: "https://trade.xshetech.com",
    tags: ["量化", "黄金", "外汇", "AI 信号"],
    stats: [
      { label: "Runtime", value: "Quant" },
      { label: "品种", value: "XAU/USD + 6" },
    ],
    hermeneCall: '"生成今日量化信号" → Quant Runtime',
  },
  {
    id: "stock_quant",
    name: "量化交易 · 股票期货",
    subtitle: "Stocks, Futures & Options Quant",
    description: "股票/期货/转债/期权/基金全品种量化。含因子选股、CTA趋势、套利策略、机器学习预测模型。",
    icon: TrendingUp,
    color: "#F472B6",
    category: "product",
    status: "可调用",
    launchUrl: "https://trade.xshetech.com/stock",
    tags: ["量化", "A股", "期货", "期权"],
    stats: [
      { label: "Runtime", value: "Quant" },
      { label: "品种", value: "A股 + 5" },
    ],
    hermeneCall: '"A股今日信号" → Quant Runtime',
  },
  {
    id: "destiny",
    name: "国学命理",
    subtitle: "AI Destiny · Traditional Metaphysics",
    description: "AI 驱动的国学命理软件：八字排盘、紫微斗数、风水罗盘、奇门遁甲。传统智慧与现代 AI 的融合。",
    icon: Sparkles,
    color: "#A78BFA",
    category: "product",
    status: "可调用",
    // v6.3.20 修复：真实命理站是玄机阁二级域名，此前误指 os.xshetech.com/destiny（SPA 壳，点进去是系统首页）
    launchUrl: "https://xuanji.xshetech.com",
    tags: ["国学", "八字", "紫微", "AI"],
    stats: [
      { label: "Runtime", value: "Destiny" },
      { label: "模块", value: "4+" },
    ],
    hermeneCall: '"排一个八字盘" → Destiny Runtime',
  },
  {
    id: "geo_dso",
    name: "GEO+DSO 增长工具",
    subtitle: "GEO + DSO Growth Engine",
    description: "公域获客增长引擎：四级关键词体系 + 三层内容梯队 + 全链路监测。AI 驱动的四大搜索平台品牌占位与流量转化系统。",
    icon: Globe,
    color: "#06B6D4",
    category: "product",
    status: "可调用",
    launchUrl: "/cockpit/workflow?tab=geo",
    tags: ["GEO", "DSO", "增长", "获客", "AI 搜索"],
    stats: [
      { label: "Runtime", value: "GEODSO" },
      { label: "平台", value: "4 大搜索" },
    ],
    hermeneCall: '"启动 GEO+DSO 获客" → GEODSO Runtime',
  },
  {
    id: "viral_analysis",
    name: "爆款分析引擎",
    subtitle: "Viral Analysis Engine · Coze 驱动",
    description: "XEAOS Coze 工作流编译器：自动采集赛道数据 → 提取爆款共性 → 生成 Content Brief（脚本模板/标题公式/封面规范/标签组合），直接输入内容工厂。",
    icon: SearchCode,
    color: "#EC4899",
    category: "product",
    status: "Beta",
    launchUrl: "/cockpit/viral-analysis",
    tags: ["爆款分析", "Coze", "Content Brief", "数据驱动"],
    stats: [
      { label: "Runtime", value: "CozeWorkflowGen" },
      { label: "模板", value: "2 平台" },
    ],
    hermeneCall: '"分析抖音AI创业赛道爆款规律" → 爆款分析引擎',
  },
  // ============ v6.3.16 官网工具矩阵（AI Works 四件套，免登录体验页）============
  // 已在 AI Works 提交/可提交：卡1口碑体检 / 卡2味检测 / 卡3口吻克隆 / 卡4关键词矩阵
  // 系统内入口 = 打开官网独立体验页（同品牌、同口吻、可评论），形成"官网引流 + 系统变现"闭环
  {
    id: "ai_visibility",
    name: "AI 口碑体检",
    subtitle: "AI Visibility Check · 官网工具",
    description: "输入品牌名，看豆包/文心/通义/DeepSeek 眼里有没有你——被提及了吗、排第几、说了好话还是坏话。30 秒出结果，免登录。",
    icon: Eye,
    color: "#38BDF8",
    category: "product",
    status: "可调用",
    launchUrl: "https://www.xshetech.com/ai-visibility.html",
    tags: ["GEO", "品牌体检", "AI 可见性", "官网工具"],
    stats: [
      { label: "状态", value: "已上线" },
      { label: "平台", value: "4 大模型" },
    ],
    hermeneCall: '"给我体检一下小舍科技的 AI 口碑" → AI 口碑体检',
  },
  {
    id: "ai_flavor",
    name: "AI 味检测器",
    subtitle: "AI Flavor Detector · 官网工具",
    description: "把文案贴进来，检测 AI 味浓度（0-100），识别网络腔/AI黑话/夸张修辞，并给出人味改写建议。免登录。",
    icon: ScanSearch,
    color: "#F59E0B",
    category: "product",
    status: "可调用",
    launchUrl: "https://www.xshetech.com/ai-flavor.html",
    tags: ["去AI味", "文案检测", "口吻", "官网工具"],
    stats: [
      { label: "状态", value: "已上线" },
      { label: "维度", value: "7 维评分" },
    ],
    hermeneCall: '"检测这篇文案的 AI 味" → AI 味检测器',
  },
  {
    id: "voice_clone",
    name: "口吻克隆",
    subtitle: "Voice Clone for Writing · 官网工具",
    description: "喂几段你的文字，AI 学会你的口吻，一键生成知乎/小红书/抖音/公众号文案。口吻是人格，平台只是载体。免登录。",
    icon: AudioLines,
    color: "#A78BFA",
    category: "product",
    status: "可调用",
    launchUrl: "https://www.xshetech.com/voice-clone.html",
    tags: ["口吻克隆", "人设", "全平台", "官网工具"],
    stats: [
      { label: "状态", value: "已上线" },
      { label: "平台", value: "4 种" },
    ],
    hermeneCall: '"用我的口吻写一篇知乎回答" → 口吻克隆',
  },
  {
    id: "geo_matrix",
    name: "GEO 关键词矩阵",
    subtitle: "Keyword Matrix Generator · 官网工具",
    description: "输入行业，一键生成四层关键词矩阵（品牌/品类/地域/业务线）+ 内容选题，让 AI 搜索时代搜得到你。免登录。",
    icon: Grid3x3,
    color: "#10B981",
    category: "product",
    status: "可调用",
    launchUrl: "https://www.xshetech.com/geo-matrix.html",
    tags: ["GEO", "关键词", "选题", "官网工具"],
    stats: [
      { label: "状态", value: "已上线" },
      { label: "层", value: "四层矩阵" },
    ],
    hermeneCall: '"生成酒店智能化行业的 GEO 关键词矩阵" → GEO 关键词矩阵',
  },
  // ============ v6.7 新增官网引流工具：官网GEO诊断 + AI问题挖掘 ============
  {
    id: "geo_website_diag",
    name: "官网GEO诊断",
    subtitle: "Website GEO Diagnosis · 官网工具",
    description: "输入网站地址，100分制诊断四大核心维度：技术可访问性 / 内容可回答性 / 品牌实体清晰度 / 证据与权威度。定位AI搜索引用短板，PDF下载与分享，免登录。",
    icon: ScanLine,
    color: "#8B5CF6",
    category: "product",
    status: "可调用",
    launchUrl: "https://www.xshetech.com/geo-diagnosis.html",
    tags: ["GEO", "官网诊断", "AI搜索", "引流工具"],
    stats: [
      { label: "评分维度", value: "4 大维度" },
      { label: "满分", value: "100 分" },
    ],
    hermeneCall: '"帮我检测小舍科技官网的GEO健康度" → 官网GEO诊断',
  },
  {
    id: "ai_question_miner",
    name: "AI问题挖掘",
    subtitle: "AI Question Miner · 官网工具",
    description: "输入行业/品牌/人群/关键词，自动挖掘全网用户正在 AI 平台提问的高热度问题（8 种类型 + 3 种挖掘模型 + 热度趋势）。一键加入品牌监测，免登录。",
    icon: Pickaxe,
    color: "#22D3EE",
    category: "product",
    status: "可调用",
    launchUrl: "https://www.xshetech.com/ai-question-miner.html",
    tags: ["GEO", "问题挖掘", "品牌监测", "引流工具"],
    stats: [
      { label: "问题类型", value: "8 种" },
      { label: "挖掘模型", value: "3 种" },
    ],
    hermeneCall: '"挖掘酒店智能化行业的AI用户问题" → AI问题挖掘',
  },
];

const CAPABILITIES: ProductCardData[] = [
  {
    id: "content_factory",
    name: "内容工厂",
    subtitle: "Content Factory · 后台能力",
    description: "支撑所有产品的内容生产能力：6 平台分类、4 种生成类型、效果预测、审核工作流、内容协议流。不对外销售，仅内部使用。",
    icon: Factory,
    color: "#A855F7",
    category: "capability",
    status: "运行中",
    launchUrl: "",
    tags: ["后台", "内容", "AI 生成", "不销售"],
    stats: [
      { label: "平台", value: "6 个" },
      { label: "生成类型", value: "4 种" },
    ],
    hermeneCall: '"生成一篇公众号推文" → 内容工厂',
  },
];

// ============================================
// P12 短板8：AI 超市外部化 MVP —— 计费模型
// 定价哑数据（Pricing）属"商业化预置定价"，非运行数据，允许预设；
// 后续接真实支付/订阅后由后端定价表驱动，此处为 MVP 评审展示。
// ============================================

/** 计费套餐档位 */
export interface PricingTier {
  name: string;
  price: string;
  unit: string;
  features: string[];
  highlight?: boolean;
}

/** 产品计费模型 */
export interface ProductPricing {
  label: string;
  currency: string;
  tiers: PricingTier[];
  note: string;
}

/** 各产品的计费模型（productId → pricing） */
const PRICING: Record<string, ProductPricing> = {
  hotel_saas: {
    label: "酒店 SaaS · 年订阅",
    currency: "¥",
    tiers: [
      { name: "体验版", price: "0", unit: "30 天试用", features: ["基础 PMS 管理", "AI 日报", "1 门店"] },
      { name: "专业版", price: "8,800", unit: "年/门店", features: ["全流程数字化", "AI 智能决策助手", "数据分析", "5 门店起"], highlight: true },
      { name: "集团版", price: "商务定制", unit: "年", features: ["多门店集团管控", "专属实施/培训", "私有化可选"] },
    ],
    note: "支持私有化部署；合同期为 1-3 年，提前续费享 9 折。",
  },
  geo_dso: {
    label: "GEO+DSO 增长引擎 · 订阅",
    currency: "¥",
    tiers: [
      { name: "轻量版", price: "3,980", unit: "年", features: ["四层关键词体系", "单品牌监测", "月度报告"] },
      { name: "增长 Pro", price: "9,800", unit: "年", features: ["3 品牌", "AI 搜索可见性监测", "竞品对标", "GEO 内容建议"], highlight: true },
      { name: "集团版", price: "商务定制", unit: "年", features: ["不限品牌", "专属优化师", "API 接入"] },
    ],
    note: "含 4 大 AI 搜索平台可见性监测；超量按次计费。",
  },
  viral_analysis: {
    label: "爆款分析引擎 · 用量",
    currency: "¥",
    tiers: [
      { name: "单次分析", price: "99", unit: "次", features: ["1 赛道爆款规律", "Content Brief 输出", "2 平台模板"] },
      { name: "包月", price: "1,990", unit: "月", features: ["不限赛道", "实时热点", "竞品矩阵"], highlight: true },
    ],
    note: "Coze 驱动；输出可直接输入内容工厂生产。",
  },
  gold_forex_quant: {
    label: "量化交易 · 内部授权",
    currency: "",
    tiers: [
      { name: "受控产品", price: "商务定制", unit: "内部", features: ["多策略回测", "实时信号", "风控风控止盈止损"] },
    ],
    note: "仅超级管理员可见；对外不销售。",
  },
  stock_quant: {
    label: "量化交易 · 内部授权",
    currency: "",
    tiers: [
      { name: "受控产品", price: "商务定制", unit: "内部", features: ["因子选股", "CTA 趋势", "套利策略"] },
    ],
    note: "仅超级管理员可见；对外不销售。",
  },
  destiny: {
    label: "国学命理 · 按次",
    currency: "¥",
    tiers: [
      { name: "单盘排盘", price: "68", unit: "次", features: ["八字/紫微", "风水罗盘", "奇门遁甲"] },
    ],
    note: "仅超级管理员可见；对外由玄机阁独立运营。",
  },
  ai_visibility: {
    label: "AI 口碑体检",
    currency: "¥",
    tiers: [
      { name: "免费体验", price: "0", unit: "次", features: ["单品牌速查", "被提及/排位/口碑"] },
      { name: "品牌监测 Pro", price: "1,280", unit: "年", features: ["持续监测", "竞品对比", "趋势预警"], highlight: true },
    ],
    note: "官网工具免费体验引流，Pro 变现。",
  },
  ai_flavor: {
    label: "AI 味检测器",
    currency: "¥",
    tiers: [
      { name: "免费体验", price: "0", unit: "次", features: ["单篇检测", "AI 味浓度", "人味改写建议"] },
      { name: "批量 Pro", price: "980", unit: "年", features: ["批量检测", "风格建议", "团队协作"], highlight: true },
    ],
    note: "按字数/篇数弹性计费可配置。",
  },
  voice_clone: {
    label: "口吻克隆",
    currency: "¥",
    tiers: [
      { name: "免费体验", price: "0", unit: "3 次", features: ["固定口吻克隆", "1 平台生成"] },
      { name: "口吻 Pro", price: "1,680", unit: "年", features: ["不限口吻", "4 平台", "人设管理"], highlight: true },
    ],
    note: "口吻为私域资产，企业版支持云端托管。",
  },
  geo_matrix: {
    label: "GEO 关键词矩阵",
    currency: "¥",
    tiers: [
      { name: "免费体验", price: "0", unit: "次", features: ["单行业矩阵", "4 层关键词"] },
      { name: "矩阵 Pro", price: "1,280", unit: "年", features: ["多行业", "内容选题", "更新提醒"], highlight: true },
    ],
    note: "支撑 GEO+DSO 内容选型。",
  },
  geo_website_diag: {
    label: "官网GEO诊断",
    currency: "¥",
    tiers: [
      { name: "免费体检", price: "0", unit: "1 次", features: ["100 分制诊断", "四大维度", "PDF 下载"] },
      { name: "优化 Pro", price: "1,980", unit: "年", features: ["持续体检", "行动清单", "追踪优化"], highlight: true },
    ],
    note: "针对官网 AI 搜索可引用性持续优化。",
  },
  ai_question_miner: {
    label: "AI问题挖掘",
    currency: "¥",
    tiers: [
      { name: "免费体验", price: "0", unit: "次", features: ["单次挖掘", "8 种问题类型", "热度趋势"] },
      { name: "持续监测 Pro", price: "1,580", unit: "年", features: ["品牌问题监测", "行业追踪", "选题落地"], highlight: true },
    ],
    note: "问题可直接转为内容选题。",
  },
};

// ============================================
// 主组件
// ============================================

// v6.6：量化 / 国学类产品仅超级管理员可见（对外不展示，避免污染主品牌 GEO 实体信号）
const RESTRICTED_PRODUCT_IDS = ["gold_forex_quant", "stock_quant", "destiny"];

export default function AIMarketplacePanel() {
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [showCapability, setShowCapability] = useState(true);
  // v5.2：GEO+DSO 控制台展开状态（AI 超市内直接启动编排）
  const [expandedGeoConsole, setExpandedGeoConsole] = useState(false);
  // P12 短板8：产品详情弹窗（含计费模型 MVP）
  const [detailProduct, setDetailProduct] = useState<ProductCardData | null>(null);
  // P2：问题→方案入口（AgentMesh 意图路由，零 token）
  const [question, setQuestion] = useState("");
  const [routeResult, setRouteResult] = useState<RouteDecision[] | null>(null);
  const [routing, setRouting] = useState(false);

  /** 输入问题 → AgentMesh 路由（零 token）→ 推荐能力 */
  const runRoute = async () => {
    if (!question.trim()) return;
    setRouting(true);
    setRouteResult(null);
    try {
      const resp: any = await RuntimeManager.query(RuntimeName.AgentMesh, {
        type: "route",
        goal: question.trim(),
        topK: 6,
      });
      // 解包 RuntimeManager 双层 data（{data:{data:{decisions}}}）
      const inner = resp?.data?.data ?? resp?.data;
      const decisions = (inner as { decisions?: RouteDecision[] })?.decisions ?? [];
      setRouteResult(decisions);
    } catch (err: any) {
      setRouteResult([]);
    } finally {
      setRouting(false);
    }
  };

  /** P2 深化：点路由决策 → 打开对应的产品/能力卡详情（无映射则保持纯信息） */
  const openLinkedProduct = (agentId: string) => {
    const pid = AGENT_TO_PRODUCT[agentId];
    if (!pid) return;
    const target = [...PRODUCTS, ...CAPABILITIES].find((p) => p.id === pid);
    if (target) setDetailProduct(target);
  };

  // v6.6：角色门控——超级管理员（元）可见全部；员工/游客隐藏量化与国学产品
  const currentUser = useUserStore((s) => s.currentUser);
  const isSuperAdmin = currentUser?.role === "super_admin";
  const visibleProducts = isSuperAdmin
    ? PRODUCTS
    : PRODUCTS.filter((p) => !RESTRICTED_PRODUCT_IDS.includes(p.id));

  const statusColor = (status: string) => {
    switch (status) {
      case "可调用": return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "运行中": return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "Beta": return "text-violet-400 bg-violet-500/10 border-violet-500/20";
      case "规划中": return "text-gray-400 bg-gray-500/10 border-gray-500/20";
      default: return "text-gray-400";
    }
  };

  return (
    <div className="h-full overflow-auto p-4 space-y-4">
      {/* ============ 头部横幅 ============ */}
      <div className="rounded-2xl border border-white/[0.08] bg-gradient-to-br from-purple-500/[0.06] to-cyan-500/[0.04] backdrop-blur-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
            <Store size={20} className="text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-white tracking-tight">
              AI 超市
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              企业所有 AI 能力的统一入口。Hermes 一句话即可调用任何产品。
            </p>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-gray-600">
            <span className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              {PRODUCTS.filter((p) => p.status === "可调用").length + 1} 可调用
            </span>
            <span className="text-gray-700">·</span>
            <span>{PRODUCTS.length + CAPABILITIES.length} 个能力</span>
          </div>
        </div>
      </div>

      {/* ============ P2：问题→方案入口（AgentMesh 意图路由，零 token） ============ */}
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
        <div className="flex items-center gap-2 mb-3">
          <RouteIcon size={12} className="text-cyan-400" />
          <span className="text-[11px] font-medium text-gray-300">问题 → 方案</span>
          <span className="text-[9px] font-mono text-gray-600">AgentMesh 意图路由 · 零 token · 已启用</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runRoute()}
            placeholder='输入你的问题，例如："把这个热点做成小红书图文"'
            className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-black/30 border border-white/[0.08] text-[11px] text-gray-200 placeholder:text-gray-600 outline-none focus:border-cyan-500/40 font-mono transition-colors"
          />
          <button
            onClick={runRoute}
            disabled={routing || !question.trim()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-mono bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {routing ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            推荐方案
          </button>
        </div>
        {/* 路由结果 */}
        {routeResult && (
          <div className="mt-3 space-y-1.5">
            {routeResult.length === 0 ? (
              <p className="text-[10px] font-mono text-gray-500">
                未命中任何能力，换个说法试试（试试「写文案 / 抖音脚本 / 小红书 / 视频封面 / 热点选题」）
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {routeResult.map((d) => {
                    const pid = AGENT_TO_PRODUCT[d.agentId];
                    const linked = pid ? [...PRODUCTS, ...CAPABILITIES].find((p) => p.id === pid) : undefined;
                    return (
                      <button
                        key={d.agentId}
                        onClick={() => openLinkedProduct(d.agentId)}
                        title={linked ? `查看 ${linked.name}` : "该能力无对应产品卡（仅信息展示）"}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-colors ${
                          linked
                            ? "bg-cyan-500/[0.08] border border-cyan-500/25 hover:bg-cyan-500/20 cursor-pointer"
                            : "bg-cyan-500/[0.06] border border-cyan-500/20 cursor-default"
                        }`}
                      >
                        <span className="text-[10px] font-mono text-gray-200">{d.agentName}</span>
                        <span className="text-[9px] font-mono text-gray-500">{ROLE_LABEL[d.role] ?? d.role}</span>
                        <span className="text-[9px] font-mono text-cyan-400">命中 {d.score}</span>
                        {linked && (
                          <span className="flex items-center gap-0.5 text-[9px] font-mono text-cyan-300">
                            <ArrowUpRight size={9} /> {linked.name}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[9px] font-mono text-gray-600">
                  点击决策卡 → 打开对应产品/能力卡详情（能力→产品联动）；无映射的保持纯信息。把这句问题告诉 Hermes，即可自动编排以上能力协同产出方案。
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* ============ 三个层次标签 ============ */}
      <div className="flex items-center gap-1.5">
        {[
          { key: "internal", label: "内部使用", active: true, color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
          { key: "external", label: "对外销售", active: false, color: "bg-gray-500/10 text-gray-500 border-gray-500/20" },
          { key: "third_party", label: "第三方上架", active: false, color: "bg-gray-500/10 text-gray-500 border-gray-500/20" },
        ].map((l) => (
          <div
            key={l.key}
            className={`px-2.5 py-1 rounded-full text-[10px] font-mono border flex items-center gap-1.5 ${l.color}`}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${l.active ? "bg-emerald-400" : "bg-gray-500"}`} />
            {l.label}
            {!l.active && <span className="text-[9px] opacity-60">规划中</span>}
          </div>
        ))}
      </div>

      {/* ============ 独立产品货架 ============ */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Zap size={12} className="text-amber-400" />
          <span className="text-[11px] font-medium text-gray-300">独立产品</span>
          <span className="text-[10px] text-gray-600 font-mono">{visibleProducts.length}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleProducts.map((product) => {
            const Hovered = hoveredProduct === product.id;
            const IconComp = product.icon;

            return (
              <div
                key={product.id}
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
                className={`relative rounded-xl border transition-all duration-200 overflow-hidden cursor-pointer group ${
                  Hovered
                    ? "border-white/[0.12] bg-white/[0.04] shadow-lg shadow-purple-500/[0.06]"
                    : "border-white/[0.06] bg-white/[0.02]"
                }`}
                onClick={() => {
                  // v5.2：GEO+DSO 卡片 → 在 AI 超市内直接打开控制台（含一键启动编排）
                  if (product.id === "geo_dso") {
                    setExpandedGeoConsole(true);
                    return;
                  }
                  // P12 短板8：有计费模型 → 先打开产品详情页（含能力与计费），用户点"进入"再跳转
                  if (PRICING[product.id]) {
                    setDetailProduct(product);
                    return;
                  }
                  if (product.launchUrl.startsWith("http")) {
                    window.open(product.launchUrl, "_blank");
                  } else if (product.launchUrl) {
                    window.location.hash = product.launchUrl;
                  }
                }}
              >
                {/* 顶部渐变条 */}
                <div
                  className="h-0.5 w-full"
                  style={{ backgroundColor: product.color }}
                />

                <div className="p-3.5">
                  {/* 卡片头 */}
                  <div className="flex items-start justify-between mb-2">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: `${product.color}18`,
                        border: `1px solid ${product.color}33`,
                      }}
                    >
                      <IconComp size={18} style={{ color: product.color }} />
                    </div>
                    <span
                      className={`text-[9px] px-2 py-0.5 rounded-full border font-mono ${statusColor(
                        product.status
                      )}`}
                    >
                      {product.status}
                    </span>
                  </div>

                  {/* 标题 */}
                  <h3 className="text-[13px] font-semibold text-white mb-0.5 group-hover:text-gray-100">
                    {product.name}
                  </h3>
                  <p className="text-[10px] text-gray-500 font-mono mb-2">
                    {product.subtitle}
                  </p>

                  {/* 描述 */}
                  <p className="text-[11px] text-gray-400 leading-relaxed mb-2.5 line-clamp-2">
                    {product.description}
                  </p>

                  {/* 标签 */}
                  <div className="flex flex-wrap gap-1 mb-2.5">
                    {product.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] text-gray-500 font-mono"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* 统计 */}
                  {product.stats && (
                    <div className="flex items-center gap-3 mb-2.5 text-[10px] text-gray-600">
                      {product.stats.map((s) => (
                        <span key={s.label} className="flex items-center gap-1">
                          <span className="text-gray-700">{s.label}</span>
                          <span className="text-gray-400 font-mono">{s.value}</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Hermes 调用提示 + 进入按钮 */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                    <span className="text-[9px] text-gray-600 font-mono truncate max-w-[65%]">
                      {product.hermeneCall}
                    </span>
                    <span
                      className="flex items-center gap-1 text-[10px] font-mono transition-colors"
                      style={{ color: product.color }}
                    >
                      进入
                      <ArrowUpRight size={10} />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ 后台能力 ============ */}
      {showCapability && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Layers size={12} className="text-violet-400" />
            <span className="text-[11px] font-medium text-gray-300">后台能力</span>
            <span className="text-[10px] text-gray-600 font-mono">不对外销售</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CAPABILITIES.map((cap) => {
              const CapIcon = cap.icon;
              return (
                <div
                  key={cap.id}
                  className="rounded-xl border border-dashed border-white/[0.05] bg-white/[0.01] p-3.5"
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: `${cap.color}14`,
                        border: `1px solid ${cap.color}33`,
                      }}
                    >
                      <CapIcon size={16} style={{ color: cap.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-[12px] font-medium text-gray-300">{cap.name}</h4>
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-gray-500/10 text-gray-500 border border-gray-500/20 font-mono">
                          {cap.status}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-1">{cap.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ 底部提示 ============ */}
      <div className="rounded-lg border border-dashed border-amber-500/10 bg-amber-500/[0.02] p-3 flex items-start gap-2">
        <Brain size={13} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[11px] text-gray-400">
            <span className="text-amber-400 font-medium">Hermes 调用方式：</span>
            在任意标签页呼出 Hermes 助手，输入产品需求即可自动路由到对应 Runtime。
            例如 "帮我分析酒店日报"、"生成今日量化信号"、"启动 GEO+DSO 获客"。
          </p>
        </div>
      </div>

      {/* ============ v5.2：GEO+DSO 控制台（AI 超市内全屏展开） ============ */}
      {expandedGeoConsole && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-[#0B0F1A]">
          {/* 顶部返回栏 */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.08] flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => setExpandedGeoConsole(false)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-[11px] font-mono text-gray-300 transition"
              >
                <ArrowLeft size={12} />
                返回 AI 超市
              </button>
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                  <Globe size={12} className="text-cyan-400" />
                </div>
                <span className="text-[12px] font-medium text-gray-200">GEO+DSO 增长控制台</span>
                <span className="text-[9px] font-mono text-gray-600">含一键启动 GrowthFactory 编排（GEO→DSO→Content→Publish）</span>
              </div>
            </div>
          </div>
          {/* GEODSOEntry 全屏 */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <GEODSOEntry />
          </div>
        </div>
      )}

      {/* ============ P12 短板8：产品详情弹窗（能力 + 计费模型 MVP） ============ */}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          pricing={PRICING[detailProduct.id]}
          onClose={() => setDetailProduct(null)}
        />
      )}
    </div>
  );
}

// ============================================
// P12 短板8：产品详情弹窗组件
// 展示：能力简介 + 标签 + 计费模型（套餐/价格/说明）+ 进入/关闭 CTA
// ============================================
function ProductDetailModal({
  product,
  pricing,
  onClose,
}: {
  product: ProductCardData;
  pricing?: ProductPricing;
  onClose: () => void;
}) {
  const IconComp = product.icon;
  const catColor = product.color;

  /** 从详情进入产品（外部链接新窗口 / 站内 hash） */
  const handleEnter = () => {
    if (product.launchUrl.startsWith("http")) {
      window.open(product.launchUrl, "_blank");
    } else if (product.launchUrl) {
      window.location.hash = product.launchUrl;
    }
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[820px] max-h-[86vh] rounded-xl border border-white/10 bg-[#0a0e1a]/95 backdrop-blur-xl overflow-hidden flex flex-col"
      >
        {/* Header：图标 + 品牌名 + 状态 */}
        <div
          className="h-1 w-full flex-shrink-0"
          style={{ backgroundColor: catColor }}
        />
        <div className="flex items-start justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${catColor}18`, border: `1px solid ${catColor}40` }}
            >
              <IconComp size={20} style={{ color: catColor }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[16px] font-semibold text-white">{product.name}</h3>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-mono border"
                  style={{ color: catColor, borderColor: `${catColor}40`, background: `${catColor}14` }}>
                  {product.category === "capability" ? "后台能力" : product.status}
                </span>
              </div>
              <p className="text-[11px] font-mono text-gray-500 mt-0.5">{product.subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* 简介 */}
          <div>
            <SectionLabel icon={Layers} color={catColor} label="产品简介" />
            <p className="text-[12px] text-gray-300 leading-relaxed">{product.description}</p>
          </div>

          {/* 标签 */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {product.tags.map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-white/[0.04] text-gray-400 border border-white/[0.06]">
                {tag}
              </span>
            ))}
          </div>

          {/* 状态指标 */}
          {product.stats && product.stats.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {product.stats.map((s) => (
                <div key={s.label} className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5">
                  <div className="text-[9px] font-mono text-gray-600 uppercase">{s.label}</div>
                  <div className="text-[11px] font-mono text-gray-300 mt-0.5">{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* 计费模型 */}
          {pricing && (
            <div>
              <SectionLabel icon={Coins} color="#F59E0B" label={pricing.label} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-2">
                {pricing.tiers.map((tier) => (
                  <div
                    key={tier.name}
                    className={`rounded-lg border p-3 flex flex-col gap-1.5 ${
                      tier.highlight
                        ? "border-purple-500/40 bg-purple-500/[0.06]"
                        : "border-white/[0.08] bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium text-gray-200">{tier.name}</span>
                      {tier.highlight && (
                        <span className="text-[8px] px-1 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono">推荐</span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-white">{pricing.currency}{tier.price}</span>
                      {tier.unit && <span className="text-[10px] font-mono text-gray-500">/ {tier.unit}</span>}
                    </div>
                    <ul className="space-y-0.5 mt-1">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-center gap-1.5 text-[10px] text-gray-400">
                          <CheckCircle2 size={10} className="text-emerald-400 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {pricing.note && (
                <p className="text-[10px] font-mono text-gray-600 mt-2">{pricing.note}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="px-5 py-3.5 border-t border-white/[0.06] flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-600">
            Hermes 调用：{product.hermeneCall}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-300 bg-white/[0.05] hover:bg-white/[0.1] transition-colors"
            >
              暂不
            </button>
            <button
              onClick={handleEnter}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[11px] font-mono text-white transition-opacity hover:opacity-100 opacity-90"
              style={{ backgroundColor: catColor }}
            >
              进入产品
              <ExternalLink size={11} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/** 小节标题（图标 + 文案） */
function SectionLabel({
  icon: Icon,
  label,
  color,
}: {
  icon: typeof Layers;
  label: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={12} style={{ color }} />
      <span className="text-[11px] font-mono uppercase tracking-wider text-gray-300">{label}</span>
    </div>
  );
}
